import { FormHelperText, TextField } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useNavigate } from "react-router-dom";
import Config from '../../config';
import { UserService } from '../../services/UserService';
import { appNote, buttonLabels } from '../shared/constants';
import DisplayContent from '../shared/display-content.component';
import { useSelector } from 'react-redux';
import { validateForm } from '../shared/form-validation';
import { AppRoutes } from '../routes/route.path';
import { ArrowBackIcon, FalconLogo, SuccessIcon } from "../../icon";
import { AuthService } from "../../services/AuthService";
import * as selector from "../../selectors/selectors";
import UnAuthComponent from "../layout/unauth.component";

interface Props { }

const ForgotPassword = (props: Props) => {
	const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
	const PageRef = useRef<HTMLDivElement>();
	const captchaCheck = useRef<any>();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [reCaptchaVerified, setReCaptchaVerified] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [errorDescription, setErrorDescription] = useState("");
	const [emailError, setEmailError] = useState("");
	const [emailSent, setEmailSent] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!!captchaCheck.current) {
			captchaCheck.current.reset();
			captchaCheck.current.execute();
		}
		const element = PageRef.current;
		if (element) {
			element.addEventListener("keydown", handleArrowNavigation);
		}
		return () => {
			const element = PageRef.current;
			if (element) {
				element.removeEventListener("keydown", handleArrowNavigation);
			}
		}
	}, [])

	const handleChange = (name, value) => {
		setHasError(false)
		setErrorDescription("")
		validateForm({ [name]: value }, (validationErrors) => {
			setEmailError(validationErrors.email || "")
		});
		setEmail(value);
	};

	const handleArrowNavigation = (e: any) => {
		const active = document.activeElement;
		let index: any;

		if (active && e.keyCode === 37) {
			index =
				active.getAttribute("data-left-arrow-index") ||
				active.getAttribute("data-arrow-index");
			(document.querySelector(`[data-arrow-index="${index}"]`) as any)?.focus();
		} else if (active && e.keyCode === 38) {
			index =
				active.getAttribute("data-up-arrow-index") ||
				active.getAttribute("data-arrow-index");
			(document.querySelector(`[data-arrow-index="${index}"]`) as any)?.focus();
		} else if (active && e.keyCode === 39) {
			index =
				active.getAttribute("data-right-arrow-index") ||
				active.getAttribute("data-arrow-index");
			(document.querySelector(`[data-arrow-index="${index}"]`) as any)?.focus();
		} else if (active && e.keyCode === 40) {
			index =
				active.getAttribute("data-down-arrow-index") ||
				active.getAttribute("data-arrow-index");
			(document.querySelector(`[data-arrow-index="${index}"]`) as any)?.focus();
		}
	};

	const forgotPassword = (event: any) => {
		event.preventDefault();
		if (reCaptchaVerified && validateForm({ email }, (validationErrors) => setEmailError(validationErrors.email || ""))) {
			setIsLoading(true);
			UserService.forgotPassword(email)
				.then((res) => {
					setEmailSent(true);
					setHasError(false);
					setReCaptchaVerified(false);
				})
				.catch((err) => {
					setReCaptchaVerified(false);
					setIsLoading(false);
					onLoadRecaptcha();
					onLoadRecaptcha();
					if (
						err &&
						err.response &&
						err.response.data &&
						err.response.data.code === "USER_NOT_ACTIVE_YET") {
						setErrorDescription(appNote.USER_NOT_ACTIVE_YET);
					} else if (
						err &&
						err.response &&
						err.response.data &&
						err.response.data.code === "MAIL_NOT_FOUND"
					) {
						setErrorDescription(appNote.INCORRECT_EMAIL_ADDRESS);

					} else if (err?.response?.data?.code === "ONLY_MICROSOFT_SSO_LOGIN_ALLOWED_FOR_THE_ORGANIZATION") {
						setErrorDescription(appNote.ONLY_MICROSOFT_LOGIN_ALLOWED)
					} else {
						setErrorDescription(err?.response?.data?.code)
					}
					setHasError(true);
				});
		}
	}
	const onLoadRecaptcha = () => {
		if (!!captchaCheck.current) {
			captchaCheck.current.reset();
			captchaCheck.current.execute();
		}
		(document.querySelector(`[data-arrow-index="2"]`) as any)?.focus();
	};

	const verifyCallback = (recaptchaToken: any) => {
		if (!!recaptchaToken) setReCaptchaVerified(true);

		setTimeout(() => {
			(document.querySelector(`[data-arrow-index="2"]`) as any)?.focus();
		}, 0);
	};

	useEffect(() => {
		if (!!AuthService.loggedIn() && !!AuthService.is2FACompleted() && !!AuthService.isNewUser()) {
			navigate(AppRoutes.RESET_PASSWORD_NEW);
		}
		else if (!!AuthService.loggedIn() && !AuthService.is2FACompleted()) {
			navigate(AppRoutes.TWO_FACTOR_AUTHENTICATION)
		}
		else if (!!AuthService.isUserLoggedIn()) {
			navigate(UserService.hasSysAdminRole() ? AppRoutes.PHONE_NUMBERS : IsMobileDevice ? AppRoutes.CONVERSATIONS : AppRoutes.DASHBOARD)
		}
		else {
			navigate(AppRoutes.FORGOT_PASSWORD, { replace: true });
		}
	}, [])

	return (
		<div className={`w-p-100 h-p-100 `} style={{ zIndex: 1 }} ref={PageRef}>
			<div className={`mx-auto ${IsMobileDevice ? "h-p-100" : "h-p-100"} d-flex flex-direction w-p-100  align-center`}>
				<div className={"d-flex h-p-20 w-p-100 align-center pl-15"}>
					<FalconLogo className="" />
				</div>
				<div className="w-p-100 h-p-60 d-flex flex-direction justify-center align-center">
					{!emailSent && <div className="t-align-center h1-medium unauth-title mb-2">
						Forgot password?
					</div>
					}
					{!emailSent ? (
						<div className={" w-p-70 max-w-50"}>
							<form onSubmit={forgotPassword}>
								<span>
									<TextField
										error={!!emailError}
										placeholder="example@domain.com"
										fullWidth
										title=""
										name="email"
										id="forgotpassword"
										className={`unauth-border mt-1 ${emailError?.length ? 'unauth-border-error' : ''}`}
										value={email}
										onChange={(e) =>
											handleChange(e.target.name, e.target.value)
										}
										variant="standard"
										label="User Name*"
										autoFocus
									/>
									<FormHelperText
										className={`${IsMobileDevice ? "h4" : "h5-regular"
											} color-primary-danger t-align-left ${emailError ? "" : "mb-2"}`}
									>
										{emailError}
									</FormHelperText>
								</span>
								<div id="recaptcha p-absolute b-1 r-2">
									<ReCAPTCHA
										ref={captchaCheck}
										tabindex={4}
										data-theme="dark"
										render="explicit"
										size={"invisible"}
										sitekey={Config.RECAPTCHA_SITE_KEY}
										asyncScriptOnLoad={onLoadRecaptcha}
										onChange={verifyCallback}
									/>
								</div>
								<button
									data-arrow-index={3}
									data-up-arrow-index={2}
									data-down-arrow-index={3}
									data-left-arrow-index={3}
									data-right-arrow-index={3}
									tabIndex={2}
									type="submit"
									className={`w-fill unauth-screen-button-gredient ${!email || emailError?.length ||
										!reCaptchaVerified ||
										!!errorDescription
										? "disabled d-block disabled-btn bg-color-disabled-btn-unauth color-secondary-light b-around-2 box-shadow-none mt-4 mb-3 "
										: "c-pointer d-block primary-btn mt-3 mb-1"
										} justify-center d-flex w-space ${IsMobileDevice ? "h2" : "h4-regular"
										}`}
								>
									{isLoading && (
										<span className="mr-1 loader-primary"></span>
									)}
									<span className={"my-auto"}>{buttonLabels.RESET_PASSWORD}</span>
								</button>
								{!!hasError && (
									<span
										className={`color-primary-danger d-flex justify-center ${IsMobileDevice ? "h4" : "h5-regular "
											}`}
									>
										{errorDescription}
									</span>
								)}
							</form>
							{!emailSent && (
								<span className={`my-auto mx-auto d-flex justify-center mt-3 align-center`}>
									<Link
										data-arrow-index={1}
										data-up-arrow-index={1}
										data-down-arrow-index={2}
										data-left-arrow-index={1}
										data-right-arrow-index={1}
										tabIndex={3}
										to={AppRoutes.SIGN_IN}
									>
										<span className="d-flex align-center">
											<ArrowBackIcon className={"c-pointer float-left w-2"}
											/>
											<span className="h4-regular ml-1 color-back-to-login d-flex">{buttonLabels.BACK_TO_LOGIN}</span>
										</span>
									</Link>
								</span>
							)}
						</div>
					) : (
						<DisplayContent
							cardTitle={"Email sent"}
							cardTitleClass={"h1-medium color-login-headers"}
							displayMsgClass={`h5-regular mx-3 color-email-sent-displaymsg t-align-center ${IsMobileDevice ? "h4" : "h5-regular"}`}
							btnClass={`c-pointer d-block t-align-center primary-btn  ${IsMobileDevice ? "h2" : "h4-regular mx-10"
								}`}
							btnLabel={buttonLabels.BACK_TO_LOGIN}
							btnPath={AppRoutes.SIGN_IN}
							displayMsg={appNote.RESETPASSWORD_SUCESS}
							displayIcon={SuccessIcon}
							IsMobileDevice={IsMobileDevice}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

export default (UnAuthComponent(ForgotPassword));